/* eslint-disable no-unsafe-optional-chaining */
import { LANGUAGE_DEFAULT, NError, NSuccess } from '@configs/const.config'
import { TResApi, TResApiErr, TResDataListApi } from '@configs/interface.config'
import { checkAuth } from '@libs/localStorage'
import { TPatchUser, TQueryUser, TSetPassword, TUser } from '@src/modules'
import { notification } from 'antd'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'

import { queryClient } from '..'
import {
  activeUsers,
  changeMultiplePointUser,
  changePointUser,
  getListUser,
  getTotalUser,
  getUserById,
  importChosenUsers,
  patchPasswordUserById,
  patchUserById,
  postUser,
  removeManyUserByIds,
  removeUserById,
  updateProfile,
} from '../apis'
import { DETAIL_OPTION, DETAIL_USER, LIST_MEMBER_INFINITE, LIST_USER, TOTAL_USER, USER_PROFILE } from '../keys'

/**
 * @method useQueryListUser
 * @param params
 * @param enabled
 * @param token
 * @returns
 */
export const useQueryListUser = (params: TQueryUser, enabled?: boolean, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TUser[]>>(
    [LIST_USER, JSON.stringify(params)],
    () => getListUser(params, accessToken),
    {
      enabled: !!accessToken && !!enabled,
    },
  )
}

/**
 * @method useQueryUserById
 * @param id
 * @param token
 * @returns
 */
export const useQueryUserById = (id: string, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<TUser>>([DETAIL_USER, id], () => getUserById(id, accessToken), {
    enabled: !!accessToken && !!id,
  })
}

/**
 * @method useQueryUserTotal
 * @param {string}token
 * @returns
 */
export const useQueryUserTotal = (token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<number>>([TOTAL_USER], () => getTotalUser(token), { enabled: !!accessToken })
}

/**
 * @method useMutationPostUser
 * @returns
 */
export const useMutationPostUser = () =>
  useMutation(postUser, {
    onSuccess: (res: TResApi<TUser>) => {
      queryClient.refetchQueries([LIST_USER]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })

/**
 * @method useMutationPatchUserById
 * @returns
 */
export const useMutationPatchUserById = () =>
  useMutation(({ id, data }: { id: string; data: TPatchUser }) => patchUserById(id, data), {
    onSuccess: (res: TResApi, { id }) => {
      queryClient.refetchQueries([LIST_USER]).then()
      queryClient.invalidateQueries([DETAIL_USER, id]).then()
      const profileData = queryClient.getQueryData<TResApi<TUser>>(USER_PROFILE)
      if (profileData?.data && profileData?.data?._id === id) {
        queryClient.refetchQueries([USER_PROFILE]).then()
      }
      notification.success({ message: NSuccess, description: res?.message })
    },
  })

/**
 * @method useMutationSetPassword
 * @returns
 */
export const useMutationSetPassword = () =>
  useMutation(({ id, data }: { id: string; data: TSetPassword }) => patchPasswordUserById(id, data), {
    onSuccess: (res: TResApi) => {
      // [TODO] ...
      notification.success({ message: NSuccess, description: res?.message })
    },
  })

/**
 * @method useMutationRemoveUserById
 * @returns
 */
export const useMutationRemoveUserById = () =>
  useMutation(removeUserById, {
    onSuccess: (res: TResApi, id) => {
      queryClient.refetchQueries([LIST_USER]).then()
      queryClient.removeQueries([DETAIL_USER, id])
      notification.success({ message: NSuccess, description: res?.message })
    },
  })

/**
 * @method useMutationRemoveManyUserByIds
 * @returns
 */
export const useMutationRemoveManyUserByIds = () =>
  useMutation(removeManyUserByIds, {
    onSuccess: (res: TResApi) => {
      queryClient.refetchQueries([LIST_USER]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })

/**
 * @method useMutationActiveUsers
 * @returns
 */
export const useMutationActiveUsers = () =>
  useMutation(({ ids, isActive }: { ids: string[]; isActive: boolean }) => activeUsers(ids, isActive), {
    onSuccess: (res: TResApi) => {
      queryClient.refetchQueries([LIST_USER]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })

export const useInfiniteListUser = (params: TQueryUser, token?: string) => {
  const accessToken = token || checkAuth()
  return useInfiniteQuery<TResDataListApi<TUser[]>>({
    queryKey: [LIST_MEMBER_INFINITE, params],
    queryFn: ({ pageParam = 1 }) => getListUser({ ...params, page: pageParam }, accessToken),
    getNextPageParam: (lastPage) =>
      lastPage?.page < Math.ceil(lastPage?.total / lastPage?.limit) ? lastPage?.page + 1 : undefined,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })
}

/**
 * @method useMutationChangePointUser
 * @returns
 */
export const useMutationChangePointUser = () =>
  useMutation(changePointUser, {
    onSuccess: (res: TResApi) => {
      // [TODO] ...
      notification.success({ message: NSuccess, description: res?.message })
      queryClient.invalidateQueries([LIST_USER]).then()
    },
  })

/**
 * @method useMutationChangePointUsers
 * @returns
 */
export const useMutationChangePointUsers = () =>
  useMutation(changeMultiplePointUser, {
    onSuccess: (res: TResApi) => {
      // [TODO] ...
      notification.success({ message: NSuccess, description: res?.message })
      queryClient.invalidateQueries([LIST_USER]).then()
    },
  })

/**
 * useMutationUpdateProfile
 * @param token
 * @returns
 */
export const useMutationUpdateProfile = (token: string) =>
  useMutation((data: TPatchUser) => updateProfile(data, token), {
    onSuccess: (res) => {
      queryClient.invalidateQueries([USER_PROFILE]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (err: Error) => {
      notification.error({ message: NError, description: err?.message })
    },
  })

/**
 * @method useMutationImportChosenUsers
 * @returns
 */
export const useMutationImportChosenUsers = (key: string[], lang = LANGUAGE_DEFAULT) =>
  useMutation(({ data }: { data: FormData }) => importChosenUsers(data), {
    onSuccess: (res: TResApi<any>) => {
      queryClient.refetchQueries([DETAIL_OPTION, JSON.stringify({ 'key[]': key }), lang]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })
